import { List as ImmutableList, Map as ImmutableMap } from "immutable";
import type { PaymentsConfig } from "../config/schema";
import type { User } from "../types/user";
import { isPaymentMethod, type PaymentMethod } from "../utils/paymentMethods";
import { useBranding } from "./BrandingProvider";

// Single source of truth for "is payment method M available right now?".
// Composes the partner-level gate (useBranding().payments) with an optional
// per-user gate for credit card (Outgoing flows pass user.credit_card_allowed;
// Books rows don't). Voucher is intentionally unconditional and not modelled.
//
// The pure resolver is exported separately so tests and non-React callers
// (selectors, SSR, etc.) can compute availability without a context provider.

export interface PaymentMethodOptions {
  // Defaults to true so callers without a per-user gate (e.g. Books rows)
  // can omit it. Outgoing flows pass user.get('credit_card_allowed', false).
  creditCardAllowedForUser?: boolean;
  // Overpay availability is partner-scoped on the backend and returned per user.
  overpayAllowedForUser?: boolean;
  // When present, the backend has already applied per-user and credential gates.
  userPaymentMethods?: PaymentMethod[];
}

export interface PaymentMethodAvailability {
  available: PaymentMethod[];
  isAvailable: (method: PaymentMethod) => boolean;
}

type UserPaymentFields = Partial<
  Pick<User, "payment_methods" | "credit_card_allowed" | "overpay_allowed">
>;

export type UserPaymentSource =
  | UserPaymentFields
  | ImmutableMap<string, unknown>
  | null
  | undefined;

const getUserValue = (
  user: UserPaymentSource,
  key: string,
  defaultValue?: unknown,
) => {
  if (!user) return defaultValue;
  if (ImmutableMap.isMap(user)) return user.get(key, defaultValue);
  return Object.prototype.hasOwnProperty.call(user, key)
    ? user[key as keyof UserPaymentFields]
    : defaultValue;
};

const getUserBoolean = (
  user: UserPaymentSource,
  key: string,
  defaultValue: boolean | undefined,
): boolean | undefined => {
  const value = getUserValue(user, key, defaultValue);
  return typeof value === "boolean" ? value : defaultValue;
};

export const normalizeUserPaymentMethods = (
  user: UserPaymentSource,
): PaymentMethod[] | undefined => {
  const paymentMethods = getUserValue(user, "payment_methods");
  const normalize = (methods: unknown[]) => methods.filter(isPaymentMethod);

  if (!paymentMethods) return undefined;
  if (Array.isArray(paymentMethods)) return normalize(paymentMethods);
  if (ImmutableList.isList(paymentMethods))
    return normalize(paymentMethods.toJS());

  return undefined;
};

export const getUserPaymentMethodOptions = (
  user: UserPaymentSource,
  defaults: PaymentMethodOptions = {},
): PaymentMethodOptions => ({
  ...defaults,
  creditCardAllowedForUser: getUserBoolean(
    user,
    "credit_card_allowed",
    defaults.creditCardAllowedForUser,
  ),
  overpayAllowedForUser: getUserBoolean(
    user,
    "overpay_allowed",
    defaults.overpayAllowedForUser,
  ),
  userPaymentMethods:
    normalizeUserPaymentMethods(user) ?? defaults.userPaymentMethods,
});

export const resolvePaymentMethods = (
  payments: PaymentsConfig,
  {
    creditCardAllowedForUser = true,
    overpayAllowedForUser = false,
    userPaymentMethods,
  }: PaymentMethodOptions = {},
): PaymentMethodAvailability => {
  const available: PaymentMethod[] = [];
  if (payments.creditCard && creditCardAllowedForUser) {
    available.push("credit_card");
  }
  if (payments.paypal) {
    available.push("paypal");
  }
  if (payments.overpay && overpayAllowedForUser) {
    available.push("overpay");
  }
  // Stripe credentials and the per-user allow flag are resolved by the
  // backend before it publishes payment_methods. Do not expose Stripe on a
  // legacy/missing list based on partner configuration alone.
  if (payments.stripe && userPaymentMethods?.includes("stripe")) {
    available.push("stripe");
  }
  const resolved = userPaymentMethods
    ? available.filter((method) => userPaymentMethods.includes(method))
    : available;

  return {
    available: resolved,
    isAvailable: (method) => resolved.includes(method),
  };
};

export const usePaymentMethods = (
  options?: PaymentMethodOptions,
): PaymentMethodAvailability =>
  resolvePaymentMethods(useBranding().payments, options);

export const getBooksPaymentMethodOptions = (
  user: UserPaymentSource,
): PaymentMethodOptions => ({
  userPaymentMethods: normalizeUserPaymentMethods(user),
});

export const useBooksPaymentMethods = (
  user: UserPaymentSource,
): PaymentMethodAvailability =>
  usePaymentMethods(getBooksPaymentMethodOptions(user));

export const useUserPaymentMethods = (
  user: UserPaymentSource,
  defaults?: PaymentMethodOptions,
): PaymentMethodAvailability =>
  usePaymentMethods(getUserPaymentMethodOptions(user, defaults));
